/* YBD Homepage v2 — multi-step form state machine + CRO tracking layer.
   Scoped under window.YBD namespace. Listens only on .ybd-form selectors.

   Tracking events emitted to dataLayer (consumed by GTM):
     - lead_step_complete    each Continue click that passes validation
     - lead_form_error       validation failure (no PII, just field name + error type)
     - lead_form_submit      successful form submit (also persists user_data to sessionStorage for Enhanced Conversions on redirect)
     - lead_form_abandon     beforeunload, if formStarted && !formSubmitted
     - lead_cta_view         IntersectionObserver fires once per session per primary CTA
     - lead_cta_click        not pushed here — GTM Click trigger handles it directly via [data-cta-location] attribute

   Lead identity:
     - lead_id generated once per session, written to hidden form field (lands in merchant email)
       and into sessionStorage. Every dataLayer event includes lead_id so the
       qualified-lead feedback loop (Apps Script -> GA4 Measurement Protocol)
       can link form submissions back to triage outcomes.
*/
(function () {
  'use strict';
  window.YBD = window.YBD || {};

  // ---------- CRM ingestion config (Sprint 3+4) ----------
  // Apps Script Web App URL — POST destination for every form submission.
  // Filled in after deploying apps_script.gs. Sheet+GA4 ingestion is a side
  // channel; Shopify's contact form still fires as the durable channel.
  var YBD_INGEST_URL = 'https://script.google.com/macros/s/AKfycbyPYhDvMkwfFu_RS5K5o6AK-EYEmfakecUVfmZUoUB4cdIEXMybT-3AEKLcV73yTdU/exec';

  // ---------- Attribution capture (gclid, utm_*, ga_client_id) ----------
  // Runs ASAP on every page load. First-touch attribution is persisted into
  // sessionStorage so a visitor who lands on a paid ad, browses, and then
  // submits the form has the original gclid/utm carried through.
  function captureAttributionOnce() {
    try {
      var params = new URLSearchParams(location.search);
      var keys = ['gclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
      var changed = false;
      var stored = JSON.parse(sessionStorage.getItem('ybd_attribution') || '{}');
      keys.forEach(function (k) {
        var v = params.get(k);
        if (v && !stored[k]) { stored[k] = v; changed = true; }
      });
      if (!stored.referrer && document.referrer) { stored.referrer = document.referrer; changed = true; }
      if (!stored.landing_page) { stored.landing_page = location.pathname; changed = true; }
      if (changed) sessionStorage.setItem('ybd_attribution', JSON.stringify(stored));
    } catch (e) { /* private mode */ }
  }
  captureAttributionOnce();

  function readGaClientIdFromCookie() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (!m) return '';
      // _ga cookie format: GA1.2.<client_id_part>.<timestamp>
      var parts = decodeURIComponent(m[1]).split('.');
      if (parts.length >= 4) return parts[2] + '.' + parts[3];
      return decodeURIComponent(m[1]);
    } catch (e) { return ''; }
  }

  function getAttribution() {
    var stored = {};
    try { stored = JSON.parse(sessionStorage.getItem('ybd_attribution') || '{}'); } catch (e) {}
    stored.ga_client_id = readGaClientIdFromCookie();
    return stored;
  }

  function sendLeadToIngestion(payload) {
    if (!YBD_INGEST_URL) return;
    try {
      // M9: defensive truncation. `fetch(..., {keepalive:true})` has a
      // 64KB body cap; a paste-bomb into the "details" textarea or a
      // freakishly long user-agent could crash the POST silently.
      var safe = Object.assign({}, payload);
      if (safe.details && safe.details.length > 8000) safe.details = safe.details.substring(0, 8000);
      if (safe.user_agent && safe.user_agent.length > 500) safe.user_agent = safe.user_agent.substring(0, 500);
      // text/plain content-type avoids CORS preflight on Apps Script Web Apps.
      // keepalive=true ensures POST survives the Shopify form redirect.
      // mode:'no-cors' means we cannot read the response — that is intentional;
      // the Shopify native contact form is the durable channel, this is the
      // additive side-channel for the Sheet + GA4 feedback loop. (M10)
      fetch(YBD_INGEST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(safe),
        keepalive: true,
        mode: 'no-cors'
      }).catch(function () { /* fire-and-forget — Shopify form is the durable channel */ });
    } catch (e) {}
  }

  // ---------- Phone auto-linking ----------
  // Theme stores the phone as plain text in announcement-bar settings and
  // footer rich-text block, so there are no <a href="tel:..."> in markup.
  // Walk text nodes once on DOMContentLoaded, wrap matching numbers in a
  // tel: anchor with data-phone-location so CHL - Click Tracker fires
  // lead_phone_click. Idempotent via window.YBD.phonesLinked.
  function wrapPhones() {
    if (window.YBD.phonesLinked) return;
    window.YBD.phonesLinked = true;
    var phoneRegex = /\+?1?\s*[( ]?(\d{3})[) ]?[\s.\-‐‑‒–—]*(\d{3})[\s.\-‐‑‒–—]*(\d{4})/;
    function locationFor(el) {
      while (el && el !== document.body) {
        var cls = (el.className && el.className.toString && el.className.toString()) || '';
        if (/announcement/i.test(cls) || el.matches && el.matches('[class*="announcement"], [class*="topbar"]')) return 'header_banner';
        if (el.tagName === 'FOOTER' || /footer/i.test(cls)) return 'footer';
        if (el.tagName === 'HEADER' || /header/i.test(cls)) return 'header';
        el = el.parentElement;
      }
      return 'page';
    }
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        if (!n.nodeValue || n.nodeValue.length < 10) return NodeFilter.FILTER_REJECT;
        if (!phoneRegex.test(n.nodeValue)) return NodeFilter.FILTER_REJECT;
        var p = n.parentNode;
        if (!p || p.tagName === 'SCRIPT' || p.tagName === 'STYLE' || p.tagName === 'A') return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var nodes = [];
    var node;
    while ((node = walker.nextNode())) nodes.push(node);
    nodes.forEach(function (textNode) {
      var match = phoneRegex.exec(textNode.nodeValue);
      if (!match) return;
      var digits = match[1] + match[2] + match[3];
      var loc = locationFor(textNode.parentElement);
      var before = textNode.nodeValue.slice(0, match.index);
      var matchedText = match[0];
      var after = textNode.nodeValue.slice(match.index + matchedText.length);
      var a = document.createElement('a');
      a.href = 'tel:+1' + digits;
      a.setAttribute('data-phone-location', loc);
      a.textContent = matchedText;
      a.style.color = 'inherit';
      a.style.textDecoration = 'inherit';
      var parent = textNode.parentNode;
      if (before) parent.insertBefore(document.createTextNode(before), textNode);
      parent.insertBefore(a, textNode);
      if (after) parent.insertBefore(document.createTextNode(after), textNode);
      parent.removeChild(textNode);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wrapPhones);
  } else {
    wrapPhones();
  }

  if (window.YBD.formInitialized) return;
  window.YBD.formInitialized = true;

  var STEP_NAMES = {
    1: 'category',
    2: 'part_details',
    3: 'contact',
    4: 'urgency'
  };

  function generateLeadId() {
    return 'ybd_' + Date.now() + '_' + Math.random().toString(36).substring(2, 10);
  }

  function dlPush(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  // ---------- Phase 4.5: engagement signals (scroll, dwell, search) ----------
  // All three fire to dataLayer with a session-scoped lead_id so the eventual
  // form-start / submit / qualified events can be joined back to engagement
  // depth. Idempotent via window.YBD flags.
  function getOrInitLeadId() {
    try {
      var existing = sessionStorage.getItem('ybd_lead_id');
      if (existing) return existing;
      var id = generateLeadId();
      sessionStorage.setItem('ybd_lead_id', id);
      return id;
    } catch (e) {
      // Private mode etc — generate ephemeral
      window.YBD._ephemeralLeadId = window.YBD._ephemeralLeadId || generateLeadId();
      return window.YBD._ephemeralLeadId;
    }
  }

  function initScrollDepth() {
    if (window.YBD.scrollDepthInit) return;
    window.YBD.scrollDepthInit = true;
    var thresholds = [25, 50, 75, 90];
    var seen = new Set();
    var leadId = getOrInitLeadId();
    function check() {
      var doc = document.scrollingElement || document.documentElement;
      var scrolled = (doc.scrollTop + window.innerHeight) / doc.scrollHeight * 100;
      thresholds.forEach(function (t) {
        if (scrolled >= t && !seen.has(t)) {
          seen.add(t);
          dlPush({
            event: 'scroll_depth',
            depth_pct: t,
            page_path: location.pathname,
            lead_id: leadId
          });
        }
      });
    }
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { check(); ticking = false; });
    }, { passive: true });
    check(); // Run once in case page is already partially scrolled (e.g. anchor links)
  }

  function initDwellMilestones() {
    if (window.YBD.dwellInit) return;
    window.YBD.dwellInit = true;
    var milestones = [15, 30, 60, 120, 300];
    var leadId = getOrInitLeadId();
    var startTime = Date.now();
    var paused = false;
    var pausedAt = 0;
    var pausedAccum = 0;
    // Pause counter when tab is hidden so background tabs don't count.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        paused = true;
        pausedAt = Date.now();
      } else if (paused) {
        pausedAccum += Date.now() - pausedAt;
        paused = false;
      }
    });
    function effectiveSeconds() {
      var now = Date.now();
      var hiddenNow = paused ? (now - pausedAt) : 0;
      return Math.floor((now - startTime - pausedAccum - hiddenNow) / 1000);
    }
    milestones.forEach(function (sec) {
      setTimeout(function fire() {
        // Recheck — if tab was hidden we may need to defer
        var elapsed = effectiveSeconds();
        if (elapsed >= sec) {
          dlPush({
            event: 'dwell_milestone',
            dwell_seconds: sec,
            page_path: location.pathname,
            lead_id: leadId
          });
        } else {
          // Re-schedule for the remaining time
          setTimeout(fire, (sec - elapsed) * 1000);
        }
      }, sec * 1000);
    });
  }

  function initSearchTracking() {
    if (window.YBD.searchInit) return;
    window.YBD.searchInit = true;
    var leadId = getOrInitLeadId();
    // (a) Detect any header search submit and push site_search.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      // Heuristic: form that looks like search (has q=… or role=search or class contains search)
      var isSearch = false;
      try {
        var action = (form.action || '').toLowerCase();
        if (action.indexOf('/search') > -1) isSearch = true;
        if (form.getAttribute('role') === 'search') isSearch = true;
        if ((form.className || '').toString().toLowerCase().indexOf('search') > -1) isSearch = true;
      } catch (err) {}
      if (!isSearch) return;
      var input = form.querySelector('input[name="q"], input[type="search"]');
      var term = input ? (input.value || '').trim() : '';
      if (!term) return;
      dlPush({
        event: 'site_search',
        search_term: term.substring(0, 100),
        page_path: location.pathname,
        lead_id: leadId
      });
    }, true);
    // (b) On a search results page, detect zero-result outcome.
    try {
      var params = new URLSearchParams(location.search);
      var q = params.get('q');
      if (q && location.pathname.indexOf('/search') > -1) {
        // Wait one tick for Shopify to render results
        setTimeout(function () {
          // Heuristic for empty results: no .product-card / .grid__item / .search-results__item
          var resultsEls = document.querySelectorAll('.product-card, .grid__item, .search-results__item, [data-search-result]');
          var emptyTextMatch = /no\s+results|did\s+not\s+match/i.test(document.body.textContent || '');
          if (resultsEls.length === 0 || emptyTextMatch) {
            dlPush({
              event: 'site_search_zero_results',
              search_term: q.substring(0, 100),
              page_path: location.pathname,
              lead_id: leadId
            });
          }
        }, 800);
      }
    } catch (err) {}
  }

  // Fire engagement-signal init at DOMContentLoaded so it runs on every page,
  // not only when the lead form is present.
  function initEngagementSignals() {
    initScrollDepth();
    initDwellMilestones();
    initSearchTracking();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEngagementSignals);
  } else {
    initEngagementSignals();
  }

  function init() {
    var form = document.getElementById('ybd-contact-form');
    var section = document.getElementById('ybd-form');

    // ---------- CTA view tracking (IntersectionObserver) ----------
    // Fires once per page load per CTA when 50% of the element is visible.
    // Runs even on pages where the form isn't present (e.g., header CTA on a product page).
    if ('IntersectionObserver' in window) {
      var seenCtas = new Set();
      var ctaObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio < 0.5) return;
          var loc = entry.target.getAttribute('data-cta-location');
          if (!loc || seenCtas.has(loc)) return;
          seenCtas.add(loc);
          dlPush({
            event: 'lead_cta_view',
            cta_location: loc,
            cta_text: (entry.target.textContent || '').trim().substring(0, 80),
            page_path: window.location.pathname
          });
        });
      }, { threshold: 0.5 });
      document.querySelectorAll('[data-cta-location]').forEach(function (el) {
        ctaObserver.observe(el);
      });
    }

    if (!form) return;

    var cloudName = section ? section.getAttribute('data-cloudinary-cloud') : '';
    var uploadPreset = section ? section.getAttribute('data-cloudinary-preset') : '';

    var steps = form.querySelectorAll('.ybd-form__step');
    var progressBar = form.querySelector('[data-progress]');
    var totalSteps = steps.length;
    var currentStep = 1;
    var uploadInProgress = false;

    // ---------- Lead identity ----------
    var leadIdField = document.getElementById('ybd-lead-id');
    var leadId = (function () {
      try {
        var existing = sessionStorage.getItem('ybd_lead_id');
        if (existing) return existing;
      } catch (e) { /* private mode */ }
      var id = generateLeadId();
      try { sessionStorage.setItem('ybd_lead_id', id); } catch (e) { /* private mode */ }
      return id;
    })();
    if (leadIdField) leadIdField.value = leadId;

    // ---------- Form-state tracking for abandon detection ----------
    var formStarted = false;
    var formSubmitted = false;
    var formStartTime = 0;
    var stepStartTime = Date.now();

    function getLastCtaLocation() {
      try { return sessionStorage.getItem('ybd_last_cta_location') || 'unknown'; }
      catch (e) { return 'unknown'; }
    }

    function markFormStarted() {
      if (formStarted) return;
      formStarted = true;
      formStartTime = Date.now();
      try { sessionStorage.setItem('ybd_form_started', '1'); } catch (e) {}
      // Phase 1.5: emit a real lead_form_start event so GA4 has a robust
      // funnel denominator. last_cta_location is read from sessionStorage
      // (CHL Click Tracker writes it on every lead_cta_click) so we can
      // attribute the start back to the originating CTA.
      dlPush({
        event: 'lead_form_start',
        lead_id: leadId,
        last_cta_location: getLastCtaLocation(),
        page_path: location.pathname
      });
    }
    // First interaction with any field marks the form as started.
    // markFormStarted is internally idempotent via the `formStarted` guard,
    // so we let both listeners stay attached for the page lifetime — the
    // prior {once: true} pattern had a subtle bug where removing the change
    // listener after one fire would silently break the radio-button path on
    // step 1 if the user backed out and re-entered. (M8)
    ['focusin', 'change'].forEach(function (ev) { form.addEventListener(ev, markFormStarted); });

    // ---------- Step state machine ----------
    function showStep(n, scroll) {
      steps.forEach(function (el) {
        var s = parseInt(el.getAttribute('data-step'), 10);
        var active = (s === n);
        el.hidden = !active;
        el.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      if (progressBar) progressBar.style.width = ((n / totalSteps) * 100) + '%';
      currentStep = n;
      stepStartTime = Date.now();
      // Scroll only on user-initiated step changes — never on initial load
      // (otherwise the form section yanks the page on entry to /).
      if (!scroll) return;
      var activeEl = form.querySelector('.ybd-form__step[data-step="' + n + '"]');
      if (activeEl) {
        var rect = activeEl.getBoundingClientRect();
        if (rect.top < 0 || rect.top > window.innerHeight * 0.5) {
          activeEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    function pushStepComplete(n) {
      // Per-(lead_id, step_number) idempotency. Without this, clicking
      // Back from step 3 to step 2 and re-completing step 2 would fire
      // a second lead_step_complete{step_number:2}, skewing GA4 funnel math.
      window.YBD._stepsFired = window.YBD._stepsFired || new Set();
      var dedupeKey = leadId + ':' + n;
      if (window.YBD._stepsFired.has(dedupeKey)) return;
      window.YBD._stepsFired.add(dedupeKey);

      var categoryEl = form.querySelector('input[name="contact[category]"]:checked');
      var urgencyEl  = form.querySelector('input[name="contact[urgency]"]:checked');
      var attachmentUrlEl = document.getElementById('ybd-attachment-url');
      dlPush({
        event: 'lead_step_complete',
        step_number: n,
        step_name: STEP_NAMES[n] || ('step_' + n),
        selected_category: categoryEl ? categoryEl.value : null,
        urgency: urgencyEl ? urgencyEl.value : null,
        has_upload: !!(attachmentUrlEl && attachmentUrlEl.value),
        time_on_step: Date.now() - stepStartTime,
        lead_id: leadId
      });
    }

    function pushFormError(n, fieldEl) {
      var errorType = 'other';
      if (fieldEl && fieldEl.validity) {
        if (fieldEl.validity.valueMissing) errorType = 'required';
        else if (fieldEl.validity.typeMismatch) errorType = 'type';
        else if (fieldEl.validity.patternMismatch) errorType = 'pattern';
        else if (fieldEl.validity.tooShort) errorType = 'too_short';
        else if (fieldEl.validity.tooLong) errorType = 'too_long';
      }
      dlPush({
        event: 'lead_form_error',
        step_number: n,
        field_name: fieldEl ? (fieldEl.name || fieldEl.id || 'unknown') : 'unknown',
        error_type: errorType,
        lead_id: leadId
      });
    }

    function validateStep(n) {
      var fieldset = form.querySelector('.ybd-form__step[data-step="' + n + '"]');
      if (!fieldset) return true;
      var requiredInputs = fieldset.querySelectorAll('input[required], textarea[required]');
      for (var i = 0; i < requiredInputs.length; i++) {
        var inp = requiredInputs[i];
        // For radio groups, check if any in the same name is selected
        if (inp.type === 'radio') {
          var name = inp.name;
          var anyChecked = !!fieldset.querySelector('input[name="' + name + '"]:checked');
          if (!anyChecked) {
            pushFormError(n, inp);
            inp.focus();
            inp.reportValidity();
            return false;
          }
        } else {
          if (!inp.checkValidity()) {
            pushFormError(n, inp);
            inp.focus();
            inp.reportValidity();
            return false;
          }
        }
      }
      return true;
    }

    form.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]');
      if (!btn) return;
      var action = btn.getAttribute('data-action');
      if (action === 'next') {
        e.preventDefault();
        markFormStarted();
        if (!validateStep(currentStep)) return;
        pushStepComplete(currentStep);
        if (currentStep < totalSteps) showStep(currentStep + 1, true);
      } else if (action === 'back') {
        e.preventDefault();
        if (currentStep > 1) showStep(currentStep - 1, true);
      }
    });

    // ---------- Cloudinary unsigned upload ----------
    // The visible file picker is decoupled from the contact form submission.
    // On file change, we POST directly to Cloudinary, then store the resulting
    // secure_url in the hidden contact[attachment_url] field. The contact form
    // posts text-only — no multipart binary, which is what was breaking
    // submissions before.
    var filePicker = document.getElementById('ybd-file-picker');
    var attachmentUrlField = document.getElementById('ybd-attachment-url');
    var uploadStatus = document.getElementById('ybd-upload-status');

    function setUploadStatus(state, message) {
      if (!uploadStatus) return;
      uploadStatus.className = 'ybd-upload-status ybd-upload-status--' + state;
      uploadStatus.textContent = message || '';
    }

    if (filePicker && cloudName && uploadPreset) {
      filePicker.addEventListener('change', function () {
        markFormStarted();
        var file = filePicker.files && filePicker.files[0];
        if (!file) {
          if (attachmentUrlField) attachmentUrlField.value = '';
          setUploadStatus('', '');
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          setUploadStatus('error', 'File is over 10 MB. Try a smaller file.');
          filePicker.value = '';
          if (attachmentUrlField) attachmentUrlField.value = '';
          return;
        }
        uploadInProgress = true;
        setUploadStatus('uploading', 'Uploading ' + file.name + '…');

        var formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', uploadPreset);

        var xhr = new XMLHttpRequest();
        xhr.open('POST', 'https://api.cloudinary.com/v1_1/' + cloudName + '/auto/upload');
        xhr.upload.onprogress = function (evt) {
          if (evt.lengthComputable) {
            var pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadStatus('uploading', 'Uploading ' + file.name + '… ' + pct + '%');
          }
        };
        xhr.onload = function () {
          uploadInProgress = false;
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              var resp = JSON.parse(xhr.responseText);
              var url = resp.secure_url || resp.url;
              if (attachmentUrlField) attachmentUrlField.value = url || '';
              setUploadStatus('success', '✓ ' + file.name + ' attached');
              dlPush({
                event: 'lead_file_upload_success',
                lead_id: leadId,
                file_size_kb: Math.round(file.size / 1024),
                file_type: file.type
              });
            } catch (e) {
              setUploadStatus('error', 'Upload succeeded but response was malformed.');
            }
          } else {
            var errMsg = 'Upload failed (HTTP ' + xhr.status + ').';
            try {
              var errResp = JSON.parse(xhr.responseText);
              if (errResp.error && errResp.error.message) errMsg = 'Upload failed: ' + errResp.error.message;
            } catch (e) { /* keep default errMsg */ }
            setUploadStatus('error', errMsg + ' You can submit without the file.');
            if (attachmentUrlField) attachmentUrlField.value = '';
          }
        };
        xhr.onerror = function () {
          uploadInProgress = false;
          setUploadStatus('error', 'Network error during upload. You can submit without the file.');
          if (attachmentUrlField) attachmentUrlField.value = '';
        };
        xhr.send(formData);
      });
    }

    // ---------- Form submit: dataLayer + sessionStorage for Enhanced Conversions ----------
    form.addEventListener('submit', function (e) {
      // Block submit while Cloudinary upload is mid-flight
      if (uploadInProgress) {
        e.preventDefault();
        setUploadStatus('uploading', 'Hold on — file is still uploading…');
        return;
      }

      formSubmitted = true;

      var emailEl = form.querySelector('input[name="contact[email]"]');
      var phoneEl = form.querySelector('input[name="contact[phone]"]');
      var nameEl  = form.querySelector('input[name="contact[name]"]');
      var companyEl = form.querySelector('input[name="contact[company]"]');
      var categoryEl = form.querySelector('input[name="contact[category]"]:checked');
      var urgencyEl = form.querySelector('input[name="contact[urgency]"]:checked');

      var userData = {
        email: emailEl && emailEl.value.trim().toLowerCase() || '',
        phone_number: phoneEl && phoneEl.value.trim() || '',
        first_name: (nameEl && nameEl.value.trim().split(' ')[0]) || '',
        last_name: (nameEl && nameEl.value.trim().split(' ').slice(1).join(' ')) || ''
      };

      // Persist user_data + lead_id to sessionStorage so the Enhanced
      // Conversions tag on /pages/request-received can pick them up after
      // Shopify's redirect.
      try {
        sessionStorage.setItem('ybd_lead_user_data', JSON.stringify(userData));
        sessionStorage.setItem('ybd_lead_meta', JSON.stringify({
          lead_id: leadId,
          company: companyEl && companyEl.value.trim() || '',
          category: categoryEl && categoryEl.value || '',
          urgency: urgencyEl && urgencyEl.value || '',
          attachment_url: attachmentUrlField && attachmentUrlField.value || '',
          submitted_at: new Date().toISOString()
        }));
      } catch (err) { /* private mode etc — fail silently */ }

      // Push immediate event to dataLayer. Includes lead_id so this submission
      // can be matched against the qualified-lead feedback later.
      // last_cta_location is the CTA the user clicked to land in the form —
      // captured by CHL Click Tracker into sessionStorage on every lead_cta_click.
      var lastCtaLocation = getLastCtaLocation();
      dlPush({
        event: 'lead_form_submit',
        user_data: userData,
        form_id: 'ybd-contact-form',
        form_version: 'v2',
        lead_id: leadId,
        category: categoryEl && categoryEl.value || '',
        urgency: urgencyEl && urgencyEl.value || '',
        has_upload: !!(attachmentUrlField && attachmentUrlField.value),
        time_in_form: formStartTime ? (Date.now() - formStartTime) : 0,
        last_cta_location: lastCtaLocation
      });

      // CRM ingestion — fire-and-forget POST to Apps Script Web App.
      // Carries full attribution + the merchant-visible details so the Sheet
      // gets every lead even if the Shopify merchant email is missed.
      // PII is sent here (intentional) — Sheet is private. Never PII to GA4.
      var attribution = getAttribution();
      var detailsEl = form.querySelector('textarea[name="contact[details]"]');
      var partEl = form.querySelector('input[name="contact[part_or_machine]"]');
      sendLeadToIngestion({
        lead_id: leadId,
        page_path: location.pathname,
        name: nameEl ? nameEl.value.trim() : '',
        company: companyEl ? companyEl.value.trim() : '',
        email: userData.email,
        phone: userData.phone_number,
        category: categoryEl ? categoryEl.value : '',
        urgency: urgencyEl ? urgencyEl.value : '',
        part_or_machine: partEl ? partEl.value.trim() : '',
        details: detailsEl ? detailsEl.value.trim() : '',
        attachment_url: attachmentUrlField ? attachmentUrlField.value : '',
        form_version: 'v2',
        last_cta_location: lastCtaLocation,
        gclid: attribution.gclid || '',
        ga_client_id: attribution.ga_client_id || '',
        utm_source: attribution.utm_source || '',
        utm_medium: attribution.utm_medium || '',
        utm_campaign: attribution.utm_campaign || '',
        utm_content: attribution.utm_content || '',
        utm_term: attribution.utm_term || '',
        referrer: attribution.referrer || document.referrer || '',
        user_agent: navigator.userAgent || ''
      });
    });

    // ---------- Form abandon detection ----------
    // Fires on page unload IF the user started the form but didn't submit.
    // Uses sendBeacon-friendly mechanism: GTM listens for this dataLayer event,
    // and the GA4 Event tag (configured with transport_type=beacon) ensures
    // the hit lands even during unload.
    window.addEventListener('beforeunload', function () {
      if (!formStarted || formSubmitted) return;
      dlPush({
        event: 'lead_form_abandon',
        last_completed_step: currentStep,
        last_step_name: STEP_NAMES[currentStep] || ('step_' + currentStep),
        time_in_form: formStartTime ? (Date.now() - formStartTime) : 0,
        lead_id: leadId
      });
    });

    // ---------- Init ----------
    var errors = form.querySelector('.ybd-form__errors');
    if (errors) {
      setTimeout(function () { errors.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 100);
    } else {
      showStep(1);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
